/**
 * StaggerList — renders children with a staggered entrance animation.
 *
 * Usage:
 *   <StaggerList>
 *     {items.map((item) => (
 *       <StaggerItem key={item.id}>
 *         <ProductCard product={item} />
 *       </StaggerItem>
 *     ))}
 *   </StaggerList>
 *
 * Or shorthand with renderItem:
 *   <StaggerList items={products} renderItem={(p) => <ProductCard key={p.id} product={p} />} />
 *
 * Each item fades + slides up with configurable stagger delay between children.
 */
'use client';

import React from 'react';
import { motion, type Variants } from 'framer-motion';
import type { ReactNode } from 'react';

const containerVariants = {
  hidden: {},
  show: (stagger: number) => ({
    transition: {
      staggerChildren: stagger,
      delayChildren: 0.05,
    },
  }),
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
  },
};

interface StaggerListProps {
  children: ReactNode;
  /** Seconds between each child animation (default 0.06) */
  stagger?: number;
  className?: string;
  as?: keyof React.JSX.IntrinsicElements;
}

export function StaggerList({
  children,
  stagger = 0.06,
  className,
  as: Tag = 'div',
}: StaggerListProps) {
  const MotionTag = motion[Tag as keyof typeof motion] as typeof motion.div;
  return (
    <MotionTag
      variants={containerVariants}
      initial="hidden"
      animate="show"
      custom={stagger}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof React.JSX.IntrinsicElements;
}

export function StaggerItem({ children, className, style, as: Tag = 'div' }: StaggerItemProps) {
  const MotionTag = motion[Tag as keyof typeof motion] as typeof motion.div;
  return (
    <MotionTag variants={itemVariants} className={className} style={style}>
      {children}
    </MotionTag>
  );
}
